/**
 * Belote's two auctions behind one AuctionModule (rules §3, §4).
 *
 * Classic: round 1 is take-or-pass on the turned card's suit; if all four
 * pass, round 2 lets each name any other suit; four more passes throw the hand
 * in. The first taker ends it.
 *
 * Coinche: an open ascending auction of 80–160 in tens plus Capot, each with a
 * trump suit. Three passes after a bid end it; four passes with no bid throw
 * it in. A seat may coinche the other side's standing bid on its turn, which
 * locks the contract: the coinched side then answers, in turn, surcoinche or
 * pass, and that is the end of the auction.
 */
import { SUIT_LABEL, SUITS, suitOf } from '../../core/cards.js';
import { BELOTE_CAPOT_VALUE, BELOTE_MAX_BID, BELOTE_MIN_BID } from './config.js';
const next = (s, n) => (s + 1) % n;
/** The two teams alternate round the table, so seats two apart are partners. */
const opponents = (a, b) => (a - b) % 2 !== 0;
export function beloteContractLabel(c) {
    const suit = SUIT_LABEL[c.trump];
    const base = c.bid === null ? suit : c.bid === 'capot' ? `Capot ${suit}` : `${c.bid}${suit}`;
    return c.mult === 4 ? `${base} ×4` : c.mult === 2 ? `${base} ×2` : base;
}
// ------------------------------------------------------------------- classic
function classicCalls(s) {
    const up = s.upcard ? suitOf(s.upcard) : null;
    const takes = s.round === 1
        ? (up ? [up] : [])
        : SUITS.filter((suit) => suit !== up);
    return [{ type: 'pass' }, ...takes.map((suit) => ({ type: 'take', suit }))];
}
function classicApply(s, seat, call) {
    const calls = [...s.calls, { seat, call }];
    if (call.type === 'take')
        return { ...s, calls, taker: { seat, suit: call.suit }, done: true };
    const passes = s.passes + 1;
    if (passes < s.seatCount)
        return { ...s, calls, passes, toAct: next(seat, s.seatCount) };
    // Rules §3.2: round 2 starts again at the dealer's right; §3.3: two full
    // rounds of passes throw the hand in.
    if (s.round === 1)
        return { ...s, calls, passes: 0, round: 2, toAct: next(s.dealer, s.seatCount) };
    return { ...s, calls, passes, done: true };
}
// ------------------------------------------------------------------- coinche
function coincheCalls(s, seat) {
    if (s.surcoincheBy.length > 0)
        return [{ type: 'pass' }, { type: 'surcoinche' }];
    const calls = [{ type: 'pass' }];
    const high = s.high;
    if (high?.value !== 'capot') {
        const floor = high ? high.value + 10 : BELOTE_MIN_BID;
        for (let v = floor; v <= BELOTE_MAX_BID; v += 10) {
            for (const suit of SUITS)
                calls.push({ type: 'bid', value: v, suit });
        }
        for (const suit of SUITS)
            calls.push({ type: 'capot', suit });
    }
    if (high && opponents(high.seat, seat))
        calls.push({ type: 'coinche' });
    return calls;
}
function coincheApply(s, seat, call) {
    const calls = [...s.calls, { seat, call }];
    const n = s.seatCount;
    switch (call.type) {
        case 'bid':
        case 'capot':
            return {
                ...s,
                calls,
                high: { seat, value: call.type === 'capot' ? 'capot' : call.value, suit: call.suit },
                passes: 0,
                toAct: next(seat, n),
            };
        case 'coinche': {
            // The coinched side answers in turn order: the seat after the coinche,
            // then its partner.
            const by = [next(seat, n), (seat + 3) % n];
            return { ...s, calls, mult: 2, surcoincheBy: by, toAct: by[0] };
        }
        case 'surcoinche':
            return { ...s, calls, mult: 4, surcoincheBy: [], done: true };
        default: {
            if (s.surcoincheBy.length > 0) {
                const rest = s.surcoincheBy.slice(1);
                return rest.length > 0 ? { ...s, calls, surcoincheBy: rest, toAct: rest[0] } : { ...s, calls, surcoincheBy: [], done: true };
            }
            const passes = s.passes + 1;
            const done = s.high === null ? passes >= n : passes >= n - 1;
            return { ...s, calls, passes, done, toAct: done ? s.toAct : next(seat, n) };
        }
    }
}
// -------------------------------------------------------------------- module
export const beloteAuction = {
    init({ config, table, dealer, faceUp }) {
        const base = { seatCount: table.seatCount, dealer, calls: [], toAct: next(dealer, table.seatCount), done: false };
        return config.variant === 'classic'
            ? { ...base, variant: 'classic', upcard: faceUp?.[0] ?? null, round: 1, passes: 0, taker: null }
            : { ...base, variant: 'coinche', high: null, passes: 0, mult: 1, surcoincheBy: [] };
    },
    legalCalls(state, seat) {
        if (state.done || seat !== state.toAct)
            return [];
        return state.variant === 'classic' ? classicCalls(state) : coincheCalls(state, seat);
    },
    apply(state, seat, call) {
        return state.variant === 'classic' ? classicApply(state, seat, call) : coincheApply(state, seat, call);
    },
    status(state) {
        if (!state.done)
            return { kind: 'open', toAct: state.toAct };
        if (state.variant === 'classic') {
            if (!state.taker)
                return { kind: 'passedOut' };
            const c = { taker: state.taker.seat, trump: state.taker.suit, variant: 'classic', bid: null, mult: 1 };
            return { kind: 'contract', contract: { ...c, label: beloteContractLabel(c) } };
        }
        if (!state.high)
            return { kind: 'passedOut' };
        const c = {
            taker: state.high.seat, trump: state.high.suit, variant: 'coinche', bid: state.high.value, mult: state.mult,
        };
        return { kind: 'contract', contract: { ...c, label: beloteContractLabel(c) } };
    },
    callKey(call) {
        switch (call.type) {
            case 'take': return `take:${call.suit}`;
            case 'bid': return `bid:${call.value}:${call.suit}`;
            case 'capot': return `capot:${call.suit}`;
            default: return call.type;
        }
    },
    describe(call, _config) {
        switch (call.type) {
            case 'pass':
                return { label: 'Pass', explain: 'Leave it to someone else.' };
            case 'take':
                return {
                    label: `Take ${SUIT_LABEL[call.suit]}`,
                    explain: `${SUIT_LABEL[call.suit]} is trump and your side must take more points than the other. You get the turned card and two more.`,
                };
            case 'bid':
                return {
                    label: `${call.value}${SUIT_LABEL[call.suit]}`,
                    explain: `${SUIT_LABEL[call.suit]} trump. Your side must take at least ${call.value} points, and more than the other side.`,
                    value: call.value,
                };
            case 'capot':
                return {
                    label: `Capot ${SUIT_LABEL[call.suit]}`,
                    explain: `${SUIT_LABEL[call.suit]} trump, and your side takes all eight tricks. Worth ${BELOTE_CAPOT_VALUE} on top of the cards.`,
                    value: BELOTE_CAPOT_VALUE,
                };
            case 'coinche':
                return { label: 'Coinche', explain: 'Double: you say they will not make it. Everything on the contract counts twice.' };
            case 'surcoinche':
                return { label: 'Surcoinche', explain: 'Redouble: you say you will. Everything on the contract counts four times.' };
        }
    },
};
//# sourceMappingURL=auction.js.map